/**
 * ICONTACTIC cart behaviour (W-129).
 *
 * 1. Keeps the complimentary eBook in the cart: added once there is something paid in the cart, removed when
 *    the cart is otherwise empty, and clamped to a single copy. Verified 2026-09-07 that a $0 line does not
 *    satisfy the minimum quantity on the "Bundle & save" automatic discounts, so the gift cannot pull a
 *    one-item order into a volume tier.
 * 2. Wires the "Add" button on the cart upsell.
 * 3. Ciralu: wires the shipping-protection toggle. The add-on is a service line: it never counts toward the
 *    badge or the "paid" count, and it leaves the cart together with the last paid item.
 *
 * Loaded once from the cart drawer, which lives in the header on every page, so it survives the section
 * re-renders that replace the cart markup.
 */
(function () {
  var cfg = window.ICT_CART || {};
  if (window.__ictCartReady) return;
  window.__ictCartReady = true;

  var busy = false;

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function isService(item) {
    return (cfg.giftHandle && item.handle === cfg.giftHandle) || (cfg.protectHandle && item.handle === cfg.protectHandle);
  }

  /** The badge counts what the shopper chose, so the gift and the protection line stay out of it. */
  function paidCount(cart) {
    if (!cart) return undefined;
    return cart.items.reduce(function (n, item) {
      return isService(item) ? n : n + item.quantity;
    }, 0);
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'icontactic-cart', itemCount: paidCount(cart) } },
      })
    );
  }

  function linesFor(cart, handle) {
    var out = [];
    if (!handle) return out;
    cart.items.forEach(function (item, i) {
      if (item.handle === handle) out.push({ line: i + 1, quantity: item.quantity });
    });
    return out;
  }

  function change(body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function add(id) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    }).then(function (r) { return r.json(); });
  }

  /** Brings the cart in line with the gift and protection rules. Idempotent: a settled cart makes no request. */
  function reconcile() {
    if (busy) return Promise.resolve();
    busy = true;
    return readCart()
      .then(function (cart) {
        var gifts = linesFor(cart, cfg.giftHandle);
        var protects = linesFor(cart, cfg.protectHandle);
        var paid = cart.items.length - gifts.length - protects.length;

        // nothing paid left: the protection has nothing to protect
        if (paid === 0 && protects.length > 0) {
          return change({ line: protects[0].line, quantity: 0 }).then(announce);
        }
        if (protects.length > 0 && protects[0].quantity !== 1) {
          return change({ line: protects[0].line, quantity: 1 }).then(announce);
        }
        if (!cfg.giftEnabled || !cfg.giftVariantId || !cfg.giftHandle) return null;
        if (paid === 0 && gifts.length > 0) {
          return change({ line: gifts[0].line, quantity: 0 }).then(announce);
        }
        if (paid > 0 && gifts.length === 0) {
          return add(cfg.giftVariantId).then(function () { return readCart(); }).then(announce);
        }
        if (gifts.length > 0 && gifts[0].quantity !== 1) {
          return change({ line: gifts[0].line, quantity: 1 }).then(announce);
        }
        return null;
      })
      .catch(function () { /* a failed reconcile must never block the cart */ })
      .then(function () { busy = false; });
  }

  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    var data = detail.data || {};
    if (data.source === 'icontactic-cart') return;
    reconcile();
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ict-upsell-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    var id = Number(button.getAttribute('data-ict-upsell-add'));
    if (!id) return;
    button.disabled = true;
    button.classList.add('is-loading');
    add(id)
      .then(function () { return readCart(); })
      .then(announce)
      .catch(function () { button.disabled = false; button.classList.remove('is-loading'); });
  });

  /* Ciralu: protection toggle. On = add one; off = remove the line. The drawer re-renders from the server, so
     the switch state always reflects the cart, never a local guess. */
  document.addEventListener('click', function (event) {
    var sw = event.target.closest && event.target.closest('[data-ict-protect-toggle]');
    if (!sw || sw.disabled) return;
    event.preventDefault();
    var id = Number(sw.getAttribute('data-ict-protect-toggle'));
    if (!id) return;
    var on = sw.getAttribute('aria-checked') === 'true';
    sw.disabled = true;
    sw.setAttribute('aria-checked', on ? 'false' : 'true');
    sw.classList.add('is-loading');
    var run = on
      ? readCart().then(function (cart) {
          var lines = linesFor(cart, cfg.protectHandle);
          return lines.length ? change({ line: lines[0].line, quantity: 0 }) : cart;
        })
      : add(id);
    run
      .then(function () { return readCart(); })
      .then(announce)
      .catch(function () { sw.disabled = false; sw.classList.remove('is-loading'); sw.setAttribute('aria-checked', on ? 'true' : 'false'); });
  });


  /* Ciralu: "reserved for mm:ss" in the drawer header. One deadline per browser session, started the first
     time a cart with items is seen; the drawer re-renders freely because the ticker only writes into whatever
     [data-ict-hold-time] exists at that second. At zero it restarts; it never blocks checkout. */
  (function () {
    var KEY = 'ictHoldUntil';
    var MINUTES = Number((document.documentElement.getAttribute('data-ict-hold-minutes')) || 10);
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var els = document.querySelectorAll('[data-ict-hold]');
      if (!els.length) return;
      var until = Number(sessionStorage.getItem(KEY) || 0);
      if (!until) { until = Date.now() + MINUTES * 60000; sessionStorage.setItem(KEY, String(until)); }
      var left = Math.round((until - Date.now()) / 1000);
      if (left <= 0) { until = Date.now() + MINUTES * 60000; sessionStorage.setItem(KEY, String(until)); left = MINUTES * 60; }
      var m = Math.floor(left / 60), s = left % 60;
      els.forEach(function (el) {
        var t = el.querySelector('[data-ict-hold-time]');
        if (t) t.textContent = pad(m) + ':' + pad(s);
      });
    }
    try { tick(); setInterval(tick, 1000); } catch (e) { /* sessionStorage blocked: no timer */ }
    document.addEventListener('cart:update', function () {
      readCart().then(function (cart) { if (!cart.items.length) sessionStorage.removeItem(KEY); }).catch(function () {});
    });
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
